import React from 'react';
import styled from 'styled-components';
import { Layout } from '../../components/page-layout';
import HeroImg from '../../assets/blog/fix-wifi.png';
import RelatedBolg from '../../components/RelatedBolg';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 64px 24px 48px 24px;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 800;
  color: #181818;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Byline = styled.p`
  text-align: center;
  color: #888;
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 700px;
  display: block;
  margin: 0 auto 2rem auto;
  border-radius: 24px;
`;

const Paragraph = styled.p`
  color: #222;
  font-size: 1.15rem;
  margin-bottom: 1.2rem;
`;

const SectionHeading = styled.h2`
  color: #F87B15;
  font-size: 1.15rem;
  font-weight: 800;
  margin: 2.5rem 0 1rem 0;
`;

const BulletList = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const Bullet = styled.li`
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.2em;
  list-style: none;
  span {
    color: #222;
    font-weight: 600;
    margin-right: 0.5em;
  }
`;

const BottomLine = styled.h3`
  font-style: italic;
  color: #F87B15;
  font-size: 1.15rem;
  margin: 2.5rem 0 1rem 0;
`;

const ProductLink = styled.a`
  color: #F87B15;
  font-weight: 800;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export default function WhySecurityCamera() {
  return (
    <Layout showCommonFooter>
      <Container>
        <Title>Why Your Security Camera Won't Connect to Wi-Fi—And How to Fix It</Title>
        <Byline>By Sensforge Team</Byline>
        <HeroImage src={HeroImg} alt="Security Camera Wi-Fi" />
        <Paragraph>
          Setting up a smart security camera should be simple. But one of the most common headaches users face is: "Why won't my camera connect to my Wi-Fi?"
        </Paragraph>
        <Paragraph>
          If you've run into this issue, you're not alone. Let's unpack the most common causes and walk through practical troubleshooting tips to get your camera online and working.
        </Paragraph>
        <SectionHeading>1. Your Wi-Fi is 5GHz Only (But the Camera Needs 2.4GHz)</SectionHeading>
        <Paragraph>
          Many smart home cameras only support 2.4GHz Wi-Fi networks—not 5GHz. This is because:
        </Paragraph>
        <BulletList>
          <Bullet><span>•</span>2.4GHz has a longer range and better wall penetration</Bullet>
          <Bullet><span>•</span>It's more stable for low-bandwidth, always-on devices</Bullet>
        </BulletList>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Check your router: Make sure the 2.4GHz band is enabled.</Bullet>
          <Bullet><span>•</span>If you have a dual-band router (2.4GHz + 5GHz), try connecting to the 2.4GHz network specifically.</Bullet>
          <Bullet><span>•</span>Some routers use the same SSID (Wi-Fi name) for both—this can confuse the camera. Try temporarily disabling 5GHz or renaming each band separately.</Bullet>
        </BulletList>
        <SectionHeading>2. Wrong Wi-Fi Password</SectionHeading>
        <Paragraph>
          It sounds simple, but it's often the culprit—especially if you recently changed your router password.
        </Paragraph>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Re-enter your password carefully (watch out for case sensitivity!).</Bullet>
          <Bullet><span>•</span>Ensure there are no special characters that might not be supported.</Bullet>
          <Bullet><span>•</span>Try connecting another device (like a phone) with the same password to double-check.</Bullet>
        </BulletList>
        <SectionHeading>3. Weak Wi-Fi Signal Where the Camera Is</SectionHeading>
        <Paragraph>
          If your camera is placed far from your router—or separated by thick walls, concrete, or appliances—the signal might not be strong enough.
        </Paragraph>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Move the camera closer to the router during setup, then reposition it after pairing.</Bullet>
          <Bullet><span>•</span>Use a Wi-Fi range extender or mesh network for better coverage in larger homes.</Bullet>
          <Bullet><span>•</span>Check signal strength on your phone in the same location to compare.</Bullet>
        </BulletList>
        <SectionHeading>4. Your Camera Needs a Clean Setup</SectionHeading>
        <Paragraph>
          If you've tried connecting before and failed, the camera might be stuck in a loop or partially configured.
        </Paragraph>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Reset the camera to factory settings (usually by holding a reset button for 5–10 seconds).</Bullet>
          <Bullet><span>•</span>Delete the camera from your app and start fresh.</Bullet>
          <Bullet><span>•</span>Make sure your phone is on the same Wi-Fi network during setup.</Bullet>
        </BulletList>
        <SectionHeading>5. Router Security Settings Are Too Strict</SectionHeading>
        <Paragraph>
          Some routers have strict firewalls, MAC address filtering, or isolation settings that prevent new devices from connecting.
        </Paragraph>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Temporarily disable MAC filtering if enabled.</Bullet>
          <Bullet><span>•</span>Turn off "AP Isolation" or "Client Isolation" in router settings.</Bullet>
          <Bullet><span>•</span>Make sure the camera is allowed to connect within the router's device list.</Bullet>
        </BulletList>
        <SectionHeading>6. You're Using a VPN or Cellular Data</SectionHeading>
        <Paragraph>
          During setup, your phone and camera need to be on the same local network. If your phone is using a VPN or mobile data, the camera won't detect it properly.
        </Paragraph>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Disable VPN during setup</Bullet>
          <Bullet><span>•</span>Make sure your phone is connected to your 2.4GHz Wi-Fi, not LTE or 5G</Bullet>
          <Bullet><span>•</span>Stay within range of the router during the process</Bullet>
        </BulletList>
        <SectionHeading>7. Firmware or App Needs an Update</SectionHeading>
        <Paragraph>
          Outdated apps or firmware can cause pairing issues.
        </Paragraph>
        <Paragraph><b>Troubleshooting Tip:</b></Paragraph>
        <BulletList>
          <Bullet><span>•</span>Check the App Store or Play Store for camera app updates</Bullet>
          <Bullet><span>•</span>If available, use the app to update your camera's firmware once it's connected</Bullet>
          <Bullet><span>•</span>Restart both the camera and your phone before setup</Bullet>
        </BulletList>
        <SectionHeading>What We're Doing to Help</SectionHeading>
        <Paragraph>
          At Sensforge, we know setup is the first impression—so we're making it as smooth as possible.
        </Paragraph>
        <BulletList>
          <Bullet><span role="img" aria-label="sparkles">✨</span> <b>Guided in-app setup</b> with smart error detection</Bullet>
          <Bullet><span role="img" aria-label="wifi">📶</span> <b>2.4GHz-only pairing</b> with clear prompts</Bullet>
          <Bullet><span role="img" aria-label="magnifying glass">🔍</span> <b>WiFi signal check tools</b> to help you find optimal camera placement</Bullet>
          <Bullet><span role="img" aria-label="repeat">🔄</span> <b>Easy reset button</b> for quick retries if something goes wrong</Bullet>
          <Bullet><span role="img" aria-label="youtube">▶️</span> YouTube tutorial videos – <b>click here!</b></Bullet>
          <Bullet><span role="img" aria-label="blog">📝</span> Blog posts like these!</Bullet>
        </BulletList>
        <SectionHeading>Looking for an indoor security camera that just works?</SectionHeading>
        <Paragraph>
          Explore the <ProductLink href="/products/indoor-pan-tilt-camera" target="_blank" rel="noopener noreferrer"><b>Sensforge Indoor Pan-Tilt Camera</b></ProductLink> — designed with intuitive setup and rock-solid connectivity.
        </Paragraph>
        <BottomLine>Final Thoughts</BottomLine>
        <Paragraph>
          Connecting a security camera to Wi-Fi doesn't have to be frustrating. Most issues come down to Wi-Fi band compatibility, signal strength, or a few simple setup missteps.
        </Paragraph>
        <Paragraph>
          By understanding these common pain points, you'll be better equipped to troubleshoot quickly and get your camera online — capturing what matters most.
        </Paragraph>
        <RelatedBolg currentSlug="why-your-security-camera-wont-connect-to-wifi" />
      </Container>
    </Layout>
  );
}

